import { Vector2 } from '../../odie';
import { arrayRandom } from '../../lib/util/jsTools';
import { GameScene } from '../GameScene';
import { MapComponent } from '../components/MapComponent';
import { BlockId } from '../defs/block';
import { config } from '../defs/config';
import { BlockGenAlgorithm } from '../defs/map';
import { MatchGroup, matchFindGroup } from './matchTools';

// BLOCK GEN ALGORITHMS
//-------------------------------------------------------------------------
function blockGenRandom(scene: GameScene) {
  return scene.getSpawnListBasic();
}
function blockGenEasy(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  // 1 cell below
  const ca = getBasicBlockId(map, column, row + 1);
  // 2 cells below
  const cb = getBasicBlockId(map, column, row + 2);
  // 1 cell to the left
  const cl = getBasicBlockId(map, column - 1, row);
  // 1 cell to the right
  const cr = getBasicBlockId(map, column + 1, row);

  if (ca) {
    clone.push(ca);
    if (ca === cb) clone.push(ca, ca);
    if (ca === cl) clone.push(ca, ca);
    if (ca === cr) clone.push(ca, ca);
    if (ca !== cl && ca !== cr && cl === cr) clone.push(ca, ca);
  }
  return clone;
}
function blockGenEasyV2Powerups(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  const finalList: BlockId[] = [];
  // for each possible spawn
  clone.forEach((color) => {
    let chances = 1; // 1 chance for a normal spawn
    // walk around the block
    walkOnBoundingBlocks(column, row, (c, r) => {
      // if the block color matches this one, measure the possible chain
      const possibleBlocks = getPossibleBasicBlockIds(map, c, r);
      // only match to the same exact color
      if (possibleBlocks.length === 1 && possibleBlocks[0] === color) {
        const group = getGroupBasedAt(scene, map, c, r);

        // if the resulting chain is a powerup
        if (
          group &&
          group.matches.length + 2 >= config.blocks.basic.powerMatch.rocket &&
          group.matches.length + 2 <= config.blocks.basic.powerMatch.cube
        ) {
          chances = 10; // 20 chances for a powerup spawn
          return;
        } else if (
          !group ||
          (group.matches.length + 2 < config.blocks.basic.powerMatch.rocket &&
            chances === 1)
        ) {
          const times = (group?.matches?.length ?? 0) + 1;
          chances = 2 * times; // 3 chances for a chain spawn
          return;
        }
      }
    });

    pushArraysIntoArray(finalList, chances, [color]);
  });

  return finalList;
}
function blockGenEasyV3GroupSpawns(
  scene: GameScene,
  map: MapComponent,
  _column: number,
  _row: number,
  _blockGen: BlockGenAlgorithm,
  groupBaseColor?: BlockId,
) {
  const clone = scene.getSpawnListBasic();
  pushArraysIntoArray(clone, 4, [groupBaseColor!]); // +4 chances for the group base color
  return clone;
}

function blockGenHard(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  // 1 cell below
  const ca = getBasicBlockId(map, column, row + 1);
  // 2 cells below
  const cb = getBasicBlockId(map, column, row + 2);
  // 1 cell to the left
  const cl = getBasicBlockId(map, column - 1, row);
  // 1 cell to the right
  const cr = getBasicBlockId(map, column + 1, row);

  let index;

  if (ca) {
    index = clone.indexOf(ca);
    if (index !== -1) clone.splice(index, 1);
  }

  if (cr) {
    index = clone.indexOf(cr);
    if (index !== -1) clone.splice(index, 1);
  }

  if (cl) {
    index = clone.indexOf(cl);
    if (index !== -1) clone.splice(index, 1);
  }

  if (!clone.length) {
    if (cr) clone.push(cr);
    else if (ca) clone.push(ca);
  }

  return clone;
}

function blockGenHardV2Corners(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  // 1 chance for those around r=1
  const blocksAround = getPossibleBlocksAround(map, column, row);

  // all other blocks have 5 chances to spawn
  clone.forEach((b) => {
    if (!blocksAround.includes(b)) {
      blocksAround.push(b, b, b, b, b); // 5 chances
    }
  });

  return blocksAround.filter((b) => clone.includes(b));
}

function blockGenHardV3Radial(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  // r=1 (uniques)
  const blocksAround = getPossibleBlocksAround(map, column, row); // 1 chance each
  // r=2 (uniques)
  const blocksAroundR2 = getPossibleBlocksAround(map, column, row, 2).filter(
    (b) => !blocksAround.includes(b),
  );
  pushArraysIntoArray(blocksAround, 2, blocksAroundR2); // 2 chances each
  // r=3 (uniques)
  const blocksAroundR3 = getPossibleBlocksAround(map, column, row, 3).filter(
    (b) => !blocksAround.includes(b),
  );
  pushArraysIntoArray(blocksAround, 3, blocksAroundR3); // 3 chances each

  // all other blocks have 5 chances to spawn
  clone.forEach((b) => {
    if (!blocksAround.includes(b)) {
      blocksAround.push(b, b, b, b, b); // 5 chances each
    }
  });

  return blocksAround.filter((b) => clone.includes(b));
}
function blockGenHardV4Columns(
  scene: GameScene,
  map: MapComponent,
  column: number,
) {
  const clone = scene.getSpawnListBasic();
  const blocksAround: BlockId[] = [];
  // all 3 columns have chance 1
  for (let r = 0; r <= map.mapDef.grid.rows; r++) {
    // column to the left
    pushArraysIntoArray(
      blocksAround,
      1,
      getPossibleBasicBlockIds(map, column - 1, r),
      getPossibleBasicBlockIds(map, column, r),
      getPossibleBasicBlockIds(map, column + 1, r),
    );
  }
  const finalList = [...new Set(blocksAround)]; // 1 chance each from all possible colors in columns

  // all other colors have a chance 5
  clone.forEach((b) => {
    if (!finalList.includes(b)) {
      finalList.push(b, b, b, b, b); // 5 chances
    }
  });

  return finalList.filter((b) => clone.includes(b));
}

function blockGenHardV5All(scene: GameScene, map: MapComponent) {
  const clone = scene.getSpawnListBasic();
  const gridPossibleColorCounts: Map<BlockId, number> = new Map();
  // go through entire grid and count how many of each possible color there are
  for (let c = 0; c < map.mapDef.grid.columns; c++) {
    for (let r = 0; r < map.mapDef.grid.rows; r++) {
      const possibleColors = getPossibleBasicBlockIds(map, c, r);
      possibleColors.forEach((color) => {
        if (gridPossibleColorCounts.has(color)) {
          gridPossibleColorCounts.set(
            color,
            gridPossibleColorCounts.get(color)! + 1,
          );
        } else {
          gridPossibleColorCounts.set(color, 1);
        }
      });
    }
  }

  const finalList: BlockId[] = [];
  const numberOfSpawnableColors = clone.length;
  // fill finalList with remaining colors at 0.1 counts
  clone.forEach((color) => {
    if (!gridPossibleColorCounts.has(color)) {
      gridPossibleColorCounts.set(color, 0.1);
    }
  });
  const sumOfCounts = Array.from(gridPossibleColorCounts.values()).reduce(
    (a, b) => a + b,
    0,
  );

  // chance for each = ceil(sum_of_all_counts*2/(number_of_spawnable_colors*block_counts))
  // if block_counts use 0.1
  clone.forEach((color) => {
    const blockCounts = gridPossibleColorCounts.get(color)!;
    const chances = Math.ceil(
      (sumOfCounts * 2) / (numberOfSpawnableColors * blockCounts),
    );
    pushArraysIntoArray(finalList, chances, [color]);
  });

  return finalList;
}

function blockGenHardV6ChainsOnTop(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  // we only care about the top row
  if (row !== 0) return clone;

  const finalList: BlockId[] = [];
  // for each possible spawn
  clone.forEach((color) => {
    let chances = 0;
    // walk around the block
    walkOnBoundingBlocks(column, row, (c, r) => {
      const entity = getEntityAt(map, c, r);
      if (!entity) return; // no block here

      // if the block is not a basic block (goal, powerup, etc) fix chance to 1
      if (entity.c.block.props.type !== 'basic') {
        chances = 1;
        return;
      }

      // if the block color matches this one, measure the possible chain
      const possibleBlocks = getPossibleBasicBlockIds(map, c, r);
      // only match to the same exact color
      if (possibleBlocks.length === 1 && possibleBlocks[0] === color) {
        const group = getGroupBasedAt(scene, map, c, r);

        // if the resulting chain is not a powerup
        if (
          group &&
          group.matches.length + 2 < config.blocks.basic.powerMatch.rocket
        ) {
          // if there no goals around matching this color, give it a chance of 5
          const hasSpecialsAround = group.neighbors.some(
            (entity) => entity.c.block.props.type !== 'basic',
          );
          if (!hasSpecialsAround && !chances) {
            chances = 3; // useless chain found
            return;
          }
        } else {
          // walk around this block
          walkOnBoundingBlocks(c, r, (c, r) => {
            const entity = getEntityAt(map, c, r);
            if (!entity) return; // no block here

            // if the block is not a basic block (goal, powerup, etc) fix chance to 1
            if (entity.c.block.props.type !== 'basic') {
              chances = 1;
              return;
            }
          });

          // a plain 2 block chain on top
          if (!chances) {
            chances = 6;
            return;
          }
        }

        // force to 1 if it forms a useful chain
        chances = 1;
      }
    });

    chances = chances || 1; // if no chain found, give it a chance of 1

    pushArraysIntoArray(finalList, chances, [color]);
  });

  return finalList;
}

function blockGenHardV8RadialWeighted(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();
  // r=1 (uniques)
  const allBlocksAround = getAllPossibleBlocksAround(map, column, row); // 1 chance each
  // r=2 (uniques)
  const allBlocksAroundR2 = getAllPossibleBlocksAround(map, column, row, 2);

  // count to a set
  const gridPossibleColorCounts = countBlocks(allBlocksAround);
  countBlocks(allBlocksAroundR2, gridPossibleColorCounts);

  const finalList: BlockId[] = [];
  const numberOfSpawnableColors = clone.length;
  // fill set with remaining colors at 0.1 counts
  clone.forEach((color) => {
    if (!gridPossibleColorCounts.has(color)) {
      gridPossibleColorCounts.set(color, 0.1);
    }
  });
  const sumOfCounts = Array.from(gridPossibleColorCounts.values()).reduce(
    (a, b) => a + b,
    0,
  );

  // chance for each = ceil(sum_of_all_counts*2/(number_of_spawnable_colors*block_counts))
  // if block_counts use 0.1
  clone.forEach((color) => {
    const blockCounts = gridPossibleColorCounts.get(color)!;
    const chances = Math.ceil(
      (sumOfCounts * 2) / (numberOfSpawnableColors * blockCounts),
    );
    pushArraysIntoArray(finalList, chances, [color]);
  });

  return finalList;
}

const largeChainsConfigs = {
  'easy-v4-larger-chains': {
    defaultSpawnChance: 1,
    chainSpawnChance: 2, // highest chance
    maxChainSize: config.blocks.basic.powerMatch.bomb,
  },
  'easy-v5-large-chains-2': {
    defaultSpawnChance: 2,
    chainSpawnChance: 3, // highest chance
    maxChainSize: config.blocks.basic.powerMatch.bomb,
  },
};
type BlockGenLargeChainsConfig = keyof typeof largeChainsConfigs;

function blockGenLargerChains(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
  blockGen: BlockGenAlgorithm,
) {
  if (!(blockGen in largeChainsConfigs)) {
    throw new Error(`blockGenLargerChains: blockGen ${blockGen} not supported`);
  }

  const clone = scene.getSpawnListBasic();
  const algoConf = largeChainsConfigs[blockGen as BlockGenLargeChainsConfig];
  const finalList: BlockId[] = [];
  // for each possible spawn
  clone.forEach((color) => {
    let chances = algoConf.defaultSpawnChance; // 1 chance for a normal spawn
    // walk around the block
    walkOnBoundingBlocks(column, row, (c, r) => {
      if (chances > 1) return; // already found a chain

      // if the block color matches this one, measure the possible chain
      const possibleBlocks = getPossibleBasicBlockIds(map, c, r);
      // only match to the same exact color
      if (possibleBlocks.length === 1 && possibleBlocks[0] === color) {
        const group = getGroupBasedAt(scene, map, c, r);

        // if the resulting is a chain, but only up to a bomb size
        if (!group || group.matches.length + 2 < algoConf.maxChainSize) {
          chances = algoConf.chainSpawnChance; // 2 chances for a chain spawn up to bomb, per block around
          return;
        }
      }
    });

    pushArraysIntoArray(finalList, chances, [color]);
  });

  return finalList;
}
const smallChainsConfigs = {
  'easy-small-chains': {
    goalsChance: 7,
    powerupsChance: 7,
    secondaryChainChance: 3,
    noChainChance: 3,
    smallChainChance: 9,
  },
  'hard-v7-small-chains': {
    goalsChance: 1,
    powerupsChance: 1,
    secondaryChainChance: 3,
    noChainChance: 6,
    smallChainChance: 9, // needs to be highest chance
  },
  'medium-small-chains': {
    goalsChance: 2,
    powerupsChance: 2,
    secondaryChainChance: 3,
    noChainChance: 5,
    smallChainChance: 8, // needs to be highest chance
  },
};
type BlockGenSmallChainsConfig = keyof typeof smallChainsConfigs;
function blockGenSmallChains(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
  blockGen: BlockGenAlgorithm,
) {
  if (!(blockGen in smallChainsConfigs)) {
    throw new Error(`blockGenSmallChains: blockGen ${blockGen} not supported`);
  }

  const clone = scene.getSpawnListBasic();
  const algoConf = smallChainsConfigs[blockGen as BlockGenSmallChainsConfig];
  const finalList: BlockId[] = [];

  // for each possible spawn
  clone.forEach((color) => {
    let chances = 0; // 1 chance for a normal spawn
    let maxChances = algoConf.smallChainChance; // 9 on original design

    // let's check what is the block below (not matching)
    const blockBelow = getPossibleBasicBlockIds(map, column, row + 1);
    if (blockBelow.length === 1 && blockBelow[0] !== color) {
      // does it form a chain?
      const group = getGroupBasedAt(scene, map, column, row);

      if (group) {
        // get the lowest block in the would-be new chain
        const lowestBlock = group.matches.reduce((a, b) =>
          a.base.c.position.mapPosition.y < b.base.c.position.mapPosition.y
            ? a
            : b,
        );
        const lowestBlockColumn = lowestBlock.base.c.position.mapPosition.x;
        const lowestBlockRow = lowestBlock.base.c.position.mapPosition.y;

        // this would be the final position of our new block
        // walk around this position and check this color would form a new chain
        walkOnBoundingBlocks(lowestBlockColumn, lowestBlockRow, (c, r) => {
          const entity = getEntityAt(map, c, r);
          if (!entity) return; // no block here

          // if the block color matches this one, cap chances to 3
          const possibleBlocks = getPossibleBasicBlockIds(map, c, r);
          // only match to the same exact color
          if (possibleBlocks.length === 1 && possibleBlocks[0] === color) {
            maxChances = algoConf.secondaryChainChance; // 3 on original design
          }
        });
      }
    }

    // walk around the block
    walkOnBoundingBlocks(column, row, (c, r) => {
      const entity = getEntityAt(map, c, r);
      if (!entity) return; // no block here

      // if the block is not a basic block (goal, powerup, etc) fix chance to minimum
      if (entity.c.block.props.type !== 'basic') {
        chances = Math.min(maxChances, algoConf.goalsChance); // 1 on original design

        // NOTE: line below added - not on original design - was a non-intended bug
        maxChances = chances;
        return;
      }

      // if the block color matches this one, measure the possible chain
      const possibleBlocks = getPossibleBasicBlockIds(map, c, r);
      // only match to the same exact color
      if (possibleBlocks.length === 1 && possibleBlocks[0] === color) {
        const group = getGroupBasedAt(scene, map, c, r);

        if (group) {
          // if the resulting chain is not a powerup
          if (
            group.matches.length + 2 <
            config.blocks.basic.powerMatch.rocket
          ) {
            // and if there no goals around matching this color, give it a chance of maxChances/9
            const hasSpecialsAround = group.neighbors.some(
              (entity) => entity.c.block.props.type !== 'basic',
            );
            if (!hasSpecialsAround && !chances) {
              // only if we didn't set a chance yet
              chances = maxChances; // ie smallChainChance
              return;
            }
          } else {
            // force to powerupsChance if it forms a useful chain
            chances = Math.min(algoConf.powerupsChance, maxChances); // 1 on original design
            maxChances = chances; // 1 on original design
          }
        }
      }
    });

    // if no chain found, give it a chance of noChainChance
    chances = chances || Math.min(maxChances, algoConf.noChainChance); // 6 on original design

    pushArraysIntoArray(finalList, chances, [color]);
  });

  return finalList;
}

/**
 * This method will always try to repeat the same color of surrounding blocks, so it can spawn an entire grid with a single color.
 * Better use it only while there are no tappable matches on the board.
 */
function blockGenEnsureMatch(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
) {
  const clone = scene.getSpawnListBasic();

  // 1 cell below
  const ca = getBasicBlockId(map, column, row + 1);
  if (ca && clone.includes(ca)) {
    return [ca];
  }

  // 1 cell to the left
  const cl = getBasicBlockId(map, column - 1, row);
  if (cl && clone.includes(cl)) {
    return [cl];
  }

  // 1 cell to the right
  const cr = getBasicBlockId(map, column + 1, row);
  if (cr && clone.includes(cr)) {
    return [cr];
  }

  // None of the surrounding blocks are available, return the entire spawn list then
  return clone;
}

// BLOCK GENERATION HELPERS
// =============================================================================
function pushArraysIntoArray<T>(
  arrDest: Array<T>,
  multiply = 1,
  ...arrsSrc: Array<Array<T>>
) {
  Array(multiply)
    .fill(0)
    .forEach(() => {
      arrsSrc.forEach((arrSrc) => {
        arrDest.push(...arrSrc);
      });
    });
}

function countBlocks(
  blocks: BlockId[],
  countsSet: Map<BlockId, number> = new Map(),
) {
  blocks.forEach((color) => {
    if (countsSet.has(color)) {
      countsSet.set(color, countsSet.get(color)! + 1);
    } else {
      countsSet.set(color, 1);
    }
  });
  return countsSet;
}

function getBasicBlockId(
  map: MapComponent,
  column: number,
  row: number,
): BlockId | undefined {
  if (!map.isPositionOnMap({ x: column, y: row })) return undefined;
  const cell = map.grid[column][row];
  const entity = cell.base?.entity;
  const blockId = entity?.c.block.blockId;

  if (!blockId || blockId.includes('cube')) return undefined;

  if (blockId === 'siren') {
    const remainingColors = Object.keys(entity.c.blockSiren.view.sirens);
    const pick = arrayRandom(remainingColors);
    switch (pick) {
      case 'blue':
        return 'basicBlue';
      case 'green':
        return 'basicGreen';
      case 'red':
        return 'basicRed';
      case 'yellow':
        return 'basicYellow';
    }
  }

  if (blockId.includes('Blue')) return 'basicBlue';
  if (blockId.includes('Green')) return 'basicGreen';
  if (blockId.includes('Purple')) return 'basicPurple';
  if (blockId.includes('Red')) return 'basicRed';
  if (blockId.includes('Yellow')) return 'basicYellow';

  return undefined;
}

function getPossibleBasicBlockIds(
  map: MapComponent,
  column: number,
  row: number,
): BlockId[] {
  if (!map.isPositionOnMap({ x: column, y: row })) return [];
  const cell = map.grid[column][row];
  const entity = cell.base?.entity;
  const blockId = entity?.c.block.blockId;

  if (!blockId || blockId.includes('cube')) return [];

  if (blockId === 'siren') {
    const remainingColors = Object.keys(entity.c.blockSiren.view.sirens);
    return remainingColors
      .map((color) => {
        switch (color) {
          case 'blue':
            return 'basicBlue';
          case 'green':
            return 'basicGreen';
          case 'red':
            return 'basicRed';
          case 'yellow':
            return 'basicYellow';
          default:
            return undefined;
        }
      })
      .filter((color) => color !== undefined) as BlockId[];
  }

  if (blockId.includes('Blue')) return ['basicBlue'];
  if (blockId.includes('Green')) return ['basicGreen'];
  if (blockId.includes('Purple')) return ['basicPurple'];
  if (blockId.includes('Red')) return ['basicRed'];
  if (blockId.includes('Yellow')) return ['basicYellow'];

  return [];
}

function walkOnBoundingBlocks(
  column: number,
  row: number,
  callback: (column: number, row: number) => void,
) {
  callback(column - 1, row); // element to the left
  callback(column + 1, row); // element to the right
  callback(column, row + 1); // element on the bottom
}

function getEntityAt(map: MapComponent, column: number, row: number) {
  const targetPosition = new Vector2(column, row);
  const targetCell = map.getCellAt(targetPosition);
  const targetEntity = targetCell?.base?.entity;

  return targetEntity;
}

function getGroupBasedAt(
  scene: GameScene,
  map: MapComponent,
  column: number,
  row: number,
): MatchGroup | undefined {
  const targetEntity = getEntityAt(map, column, row);

  if (!targetEntity) return undefined; // should never happen anyway

  // find the group
  return matchFindGroup(scene, targetEntity);
}

function getAllPossibleBlocksAround(
  map: MapComponent,
  column: number,
  row: number,
  radius = 1,
): BlockId[] {
  const blocksAround: BlockId[] = [];
  // all the rows top to bottom on the left and right side
  for (let r = row - radius; r <= row + radius; r++) {
    pushArraysIntoArray(
      blocksAround,
      1,
      getPossibleBasicBlockIds(map, column - radius, r),
      getPossibleBasicBlockIds(map, column + radius, r),
    );
  }
  // all the rows underneath
  for (let c = column - radius + 1; c <= column + radius - 1; c++) {
    pushArraysIntoArray(
      blocksAround,
      1,
      getPossibleBasicBlockIds(map, c, row + radius),
    );
  }
  return blocksAround; // totals
}

function getPossibleBlocksAround(
  map: MapComponent,
  column: number,
  row: number,
  radius = 1,
): BlockId[] {
  return [...new Set(getAllPossibleBlocksAround(map, column, row, radius))]; // uniques
}

export const blockGeneration = {
  random: blockGenRandom,
  easy: blockGenEasy,
  'easy-v2-powerups': blockGenEasyV2Powerups,
  'easy-v3-group-spawns': blockGenEasyV3GroupSpawns,
  'easy-v4-larger-chains': blockGenLargerChains,
  'easy-v5-large-chains-2': blockGenLargerChains,
  'easy-small-chains': blockGenSmallChains,
  'medium-small-chains': blockGenSmallChains,
  hard: blockGenHard,
  'hard-v2-corners': blockGenHardV2Corners,
  'hard-v3-radial': blockGenHardV3Radial,
  'hard-v4-columns': blockGenHardV4Columns,
  'hard-v5-all': blockGenHardV5All,
  'hard-v6-chains-on-top': blockGenHardV6ChainsOnTop,
  'hard-v7-small-chains': blockGenSmallChains,
  'hard-v8-radial-weighted': blockGenHardV8RadialWeighted,
  'ensure-match': blockGenEnsureMatch,
};
